/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { TABULAR_VIEWS, consoleCommands, events } from 'modules/common/ui-controller';
import { createVisualCollapseStep } from 'modules/common/ui-controller/utils/shared-command-steps';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { trendActions } from '../slice';

// time the trend visuals stay hidden before the visual collapse command value is applied
const VISUAL_COLLAPSE_DELAY = 5000;
// time given to the visuals to be displayed again before the ribbon view commands are applied
const RIBBON_VIEW_DELAY = 4000;
// actions to show or hide the visuals of each trend view
const VISUAL_DISPLAY_ACTIONS = {
  [REPORT_TYPE.TREND_TIME]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: trendActions.setRevenueIsDisplayTT,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: trendActions.setOccupiedRoomIsDisplayTT,
    [TABULAR_VIEWS.ADR]: trendActions.setAdrIsDisplayTT,
    [TABULAR_VIEWS.BOOKING_WINDOW]: trendActions.setAvgBookingWindowIsDisplayTT,
    [TABULAR_VIEWS.LENGTH_OF_STAY]: trendActions.setAvgLosIsDisplayTT,
    [TABULAR_VIEWS.CANCELLATIONS]: trendActions.setCxlIsDisplayTT,
  },
  [REPORT_TYPE.TREND_SEGMENT]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: trendActions.setRevenueIsDisplayTS,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: trendActions.setOccupiedRoomIsDisplayTS,
    [TABULAR_VIEWS.ADR]: trendActions.setAdrIsDisplayTS,
    [TABULAR_VIEWS.BOOKING_WINDOW]: trendActions.setAvgBookingWindowIsDisplayTS,
    [TABULAR_VIEWS.LENGTH_OF_STAY]: trendActions.setAvgLosIsDisplayTS,
    [TABULAR_VIEWS.CANCELLATIONS]: trendActions.setCxlIsDisplayTS,
  },
  [REPORT_TYPE.TREND_HOTEL]: {
    [TABULAR_VIEWS.TOTAL_REVENUE]: trendActions.setRevenueIsDisplayTH,
    [TABULAR_VIEWS.TOTAL_OCCUPIED_ROOMS]: trendActions.setOccupiedRoomIsDisplayTH,
    [TABULAR_VIEWS.ADR]: trendActions.setAdrIsDisplayTH,
    [TABULAR_VIEWS.BOOKING_WINDOW]: trendActions.setAvgBookingWindowIsDisplayTH,
    [TABULAR_VIEWS.LENGTH_OF_STAY]: trendActions.setAvgLosIsDisplayTH,
    [TABULAR_VIEWS.CANCELLATIONS]: trendActions.setCxlIsDisplayTH,
  },
};
// active view command is in the url and the view is not opened yet
const isActiveViewPending = (context, { pending }) =>
  Object.hasOwn(pending, consoleCommands.ACTIVE_TREND_TAB) &&
  String(pending[consoleCommands.ACTIVE_TREND_TAB]) !== context.activeTab;
// ribbon view commands are available only when the ribbon view is enabled
const createRibbonViewStep = (command, event, action, delayBefore) => ({
  command,
  event,
  ready: (context) => context.isLoaded,
  delayBefore,
  apply: (value, { dispatch, isTsRibbonViewDisabled }) => {
    if (!isTsRibbonViewDisabled) {
      dispatch(action(value === 1));
    }
  },
});
/**
 * Trend tab visual collapse and ribbon view commands, applied after the filter, pms sync and
 * vi response commands. context needs activeTab and isTsRibbonViewDisabled.
 */
const TREND_COLLAPSE_COMMAND_STEPS = [
  // active view and symmetric comparison collapse commands are read by the visual collapse step
  {
    command: consoleCommands.ACTIVE_TREND_TAB,
    event: events.SET_TREND_ACTIVE_TAB,
    listenOnly: true,
  },
  {
    command: consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE,
    event: events.SET_TREND_SYMMETRIC_COMPARISON_COLLAPSE,
    listenOnly: true,
  },
  createVisualCollapseStep(
    events.SET_TREND_VISUAL_COLLAPSE,
    VISUAL_COLLAPSE_DELAY,
    (context) => VISUAL_DISPLAY_ACTIONS[context.activeTab],
    {
      requires: [consoleCommands.ACTIVE_TREND_TAB, consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE],
      // visuals of the view in the url are collapsed, wait until that view is opened
      ready: (context, run) => !isActiveViewPending(context, run),
      start: (value, { dispatch }) => {
        dispatch(trendActions.setSymmetricComparisonIsDisplay(false));
      },
      apply: (value, { dispatch }, { pending }) => {
        // symmetric comparison visual is displayed again when the url has no collapse command for it
        const isDisplay = Object.hasOwn(pending, consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE)
          ? pending[consoleCommands.SYMMETRIC_COMPARISON_COLLAPSE] === 1
          : true;
        dispatch(trendActions.setSymmetricComparisonIsDisplay(isDisplay));
      },
    }
  ),
  createRibbonViewStep(
    consoleCommands.RRV,
    events.SET_TREND_RRV,
    trendActions.setIsTsRevenueRibbonView,
    RIBBON_VIEW_DELAY
  ),
  createRibbonViewStep(
    consoleCommands.ROV,
    events.SET_TREND_ROV,
    trendActions.setIsTsOccupiedRoomsRibbonView
  ),
  createRibbonViewStep(
    consoleCommands.RAV,
    events.SET_TREND_RAV,
    trendActions.setIsTsAdrRibbonView
  ),
];

export default TREND_COLLAPSE_COMMAND_STEPS;
